/** Lets SignalR push server clock without importing React context. */
let serverTimeHandler = null;
let serverTimeOffsetMs = null;

const normalizeApiDateTime = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  return raw.includes("T") ? raw : raw.replace(" ", "T");
};

/** Parses central-time API strings like `2026-06-18 19:03:39` (no timezone conversion). */
export const parseServerTimeIso = (value) => {
  const normalized = normalizeApiDateTime(value);
  if (!normalized) return null;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const registerServerTimeHandler = (handler) => {
  serverTimeHandler = typeof handler === "function" ? handler : null;
};

const syncServerTimeOffset = (serverTimeIso) => {
  const parsed = parseServerTimeIso(serverTimeIso);
  if (parsed) {
    serverTimeOffsetMs = parsed.getTime() - Date.now();
  }
};

/** Same clock source used by the header — falls back to local time until socket sync. */
export const getCurrentServerTime = () => {
  if (serverTimeOffsetMs !== null) {
    return new Date(Date.now() + serverTimeOffsetMs);
  }
  return new Date();
};

export const pushServerTime = (payload) => {
  if (payload?.serverTimeIso) {
    syncServerTimeOffset(payload.serverTimeIso);
  }
  serverTimeHandler?.(payload);
};

/** Elapsed break seconds: previous breakTimeSeconds + (serverTimeIso - breakStartIso). */
export const getBreakElapsedSeconds = (
  breakStartIso,
  currentTime = new Date(),
  previousBreakSeconds = 0
) => {
  const breakStart = parseServerTimeIso(breakStartIso);
  if (!breakStart) {
    return Math.max(0, Math.floor(Number(previousBreakSeconds) || 0));
  }

  const current =
    currentTime instanceof Date
      ? currentTime
      : parseServerTimeIso(currentTime) ?? new Date();

  const currentBreakSeconds = Math.max(
    0,
    Math.floor((current.getTime() - breakStart.getTime()) / 1000)
  );
  const previousSeconds = Math.max(0, Math.floor(Number(previousBreakSeconds) || 0));

  return previousSeconds + currentBreakSeconds;
};

export const getBreakElapsedSecondsFromClock = (clock, currentTime = null) => {
  const current =
    currentTime instanceof Date
      ? currentTime
      : currentTime ?? parseServerTimeIso(clock?.serverTimeIso) ?? new Date();

  return getBreakElapsedSeconds(
    clock?.breakStartIso,
    current,
    clock?.breakTimeSeconds
  );
};

export const formatBreakTimer = (totalSeconds) => {
  const secs = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(secs / 3600);
  const minutes = Math.floor((secs % 3600) / 60);
  const seconds = secs % 60;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};
